/**
 * DÓNDE ESTÁ QUIEN REPORTA, según el GPS (22-09-2026, Pablo: «mientras analiza
 * el audio, que analice la ubicación y ya se coloque más o menos»).
 *
 * Con el punto ya proyectado al marco local de UNA nave (metros), decide el
 * SECTOR (el polígono que lo contiene) y la LÍNEA más cercana de ese sector.
 * El sector es fiable (son grandes); la línea es una SUGERENCIA (las líneas van
 * a 1–2 m y el GPS a ±5–10 m): se enseña marcada y se confirma con un toque.
 *
 * Puro: sin red, sin React, sin reloj.
 */
import { nearestPointOnSegment, pointInRing, type XY } from "@/lib/geo/wgs84Local";

export interface PlacementBlock {
  id: string;
  /** `blocks.geometry.points`, en metros del marco de la nave. */
  points: XY[];
}

export interface PlacementRow {
  id: string;
  block_id: string;
  line_number: number;
  a: XY;
  b: XY;
}

export interface Placement {
  blockId: string;
  lineNumber: number | null;
  rowId: string | null;
  /** Distancia del punto a la línea sugerida, en metros. */
  distanceM: number | null;
}

/** Peor que esto y no se adivina nada: a ±25 m el GPS ni siquiera acierta el sector. */
export const MAX_ACCURACY_M = 25;

export function placeInGreenhouse(local: XY, blocks: readonly PlacementBlock[], rows: readonly PlacementRow[]): Placement | null {
  const block = blocks.find((b) => b.points.length >= 3 && pointInRing(local, b.points));
  if (!block) return null;
  let best: { row: PlacementRow; d: number } | null = null;
  for (const r of rows) {
    if (r.block_id !== block.id) continue;
    const near = nearestPointOnSegment(local, r.a, r.b);
    const d = Math.hypot(local.x - near.point.x, local.y - near.point.y);
    if (!best || d < best.d) best = { row: r, d };
  }
  return { blockId: block.id, lineNumber: best?.row.line_number ?? null, rowId: best?.row.id ?? null, distanceM: best ? Math.round(best.d * 10) / 10 : null };
}

/** Una nave cuyo perímetro contiene el punto, con el punto en su marco y a qué distancia del borde. */
export interface GreenhouseHit {
  id: string;
  local: XY;
  /** Metros hasta el borde más cercano: cuanto más, más claro que está DENTRO. */
  depth: number;
}

/**
 * VARIAS naves contienen el punto (naves solapadas: pasa en la demo, y puede
 * pasar con dos dibujos torcidos). Gana la primera, ordenadas de más adentro a
 * menos, en la que el punto caiga dentro de un SECTOR; si en ninguna cae en un
 * sector, la más adentro. Determinista y con sentido: la nave en la que más
 * claramente está, y de esas la que sabe decir en qué sector.
 */
export function chooseGreenhouse(
  hits: readonly GreenhouseHit[],
  blocks: readonly (PlacementBlock & { greenhouseId: string })[],
  rows: readonly PlacementRow[],
): { hit: GreenhouseHit; placement: Placement | null } | null {
  const ordered = [...hits].sort((a, b) => b.depth - a.depth);
  for (const hit of ordered) {
    const placement = placeInGreenhouse(hit.local, blocks.filter((b) => b.greenhouseId === hit.id), rows);
    if (placement) return { hit, placement };
  }
  return ordered[0] ? { hit: ordered[0], placement: null } : null;
}
